import React, { useState } from "react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import PapperBlock from "../../../componentes/PapperBlock/PapperBlock";
import papperStyles from "../../../componentes/PapperBlock/papperStyle-jss";
import Comparativo from "./abas/comparativo";
import Vendas from "./abas/vendas";
import Pagamentos from "./abas/pagamentos";
import Antecipacoes from "./abas/antecipacoes";
import Previsoes from "./abas/previsoes";
import RegistroPay from "./abas/registroPay";
import LiberaAcesso from "../Home/liberaAcesso";
import LimiteErroTela from "../Integracao/limiteErroTela";
import { AbaPeriodo, FiltroHomeProvider, FiltrosPainel } from "./contexto/filtroHome";
import { PainelVisivelContext } from "./contexto/painelVisivel";
import { ABA_INATIVA, BORDA_ABA, FONTE, FUNDO_CARTAO, NAVY, TEXTO_SOBRE_COR } from "./estilos/tokens";

const ABAS: {
  rotulo: string;
  conteudo: React.ComponentType;
  periodo?: AbaPeriodo;
  filtroSemanal?: boolean;
  filtroVendasAte?: boolean;
  semFiltros?: boolean;
}[] = [
  { rotulo: "Comparativo", conteudo: Comparativo, filtroSemanal: true },
  { rotulo: "Vendas", conteudo: Vendas, periodo: "VENDA" },
  { rotulo: "Pagamentos", conteudo: Pagamentos, periodo: "PAGAMENTO" },
  { rotulo: "Antecipações", conteudo: Antecipacoes, periodo: "ANTECIPADO" },
  { rotulo: "Previsões", conteudo: Previsoes, periodo: "PREVISAO", filtroVendasAte: true },
  { rotulo: "Registro Pay", conteudo: RegistroPay, semFiltros: true },
];

const ABA_INICIAL = ABAS.findIndex((aba) => aba.rotulo === "Vendas");

const idAba = (indice: number) => `home-aba-${indice}`;
const idPainel = (indice: number) => `home-painel-${indice}`;

const OFFSET_LATERAL_PAPPERBLOCK = -24;
const BORDA_CARD = 1;

const estiloAbaBase = {
  textTransform: "none" as "none",
  fontFamily: FONTE,
  fontSize: 20,
  fontWeight: 600,
  minWidth: "auto",
  minHeight: 48,
  padding: "6px 20px",
  whiteSpace: "nowrap" as "nowrap",
  opacity: 1,
  transition: "background-color .15s ease, color .15s ease",
};

export const estiloAba = (
  ativa: boolean,
  primeira: boolean,
  ultima: boolean,
  compacta: boolean
) => ({
  ...estiloAbaBase,
  fontSize: compacta ? 15 : estiloAbaBase.fontSize,
  padding: compacta ? "6px 12px" : estiloAbaBase.padding,
  backgroundColor: ativa ? ABA_INATIVA : FUNDO_CARTAO,
  color: ativa ? TEXTO_SOBRE_COR : NAVY,
  border: ativa ? `1px solid ${ABA_INATIVA}` : "none",
  borderBottom: `1px solid ${ativa ? ABA_INATIVA : BORDA_ABA}`,
  borderTopLeftRadius: primeira ? 0 : 12,
  borderTopRightRadius: ultima ? 0 : 12,
});

const DashboardNovo = () => {
  const [abaAtiva, setAbaAtiva] = useState(ABA_INICIAL);
  // Abas ja visitadas permanecem montadas e apenas ocultas, senao voltar para uma
  // delas refaz todas as requisicoes do painel.
  const [visitadas, setVisitadas] = useState<number[]>([ABA_INICIAL]);

  const trocarAba = (novaAba: number) => {
    setAbaAtiva(novaAba);
    setVisitadas((anteriores) =>
      anteriores.indexOf(novaAba) >= 0 ? anteriores : anteriores.concat(novaAba)
    );
  };

  const tema = useTheme();
  // as abas encostam na borda de cima do card, entao o recuo precisa cancelar
  // exatamente o padding interno do PapperBlock
  // e tambem a borda de 1px do card, senao ela aparece como uma linha acima das abas
  const estilosPapper = papperStyles(tema) as any;
  const offsetTopo = -(estilosPapper.root.paddingTop + estilosPapper.content.padding + BORDA_CARD);
  const abasRolam = useMediaQuery(tema.breakpoints.down("md"));
  const telaPequena = useMediaQuery(tema.breakpoints.down("sm"));
  const offsetLateral = (telaPequena ? -10 : OFFSET_LATERAL_PAPPERBLOCK) - BORDA_CARD;

  return (
    <LiberaAcesso>
      <PapperBlock title="Home" icon="ios-stats" desc="">
        <FiltroHomeProvider>
          <Tabs
            value={abaAtiva}
            onChange={(_evento, novaAba) => trocarAba(novaAba)}
            textColor="primary"
            variant={abasRolam ? "scrollable" : "fullWidth"}
            scrollButtons={abasRolam ? "on" : "off"}
            TabIndicatorProps={{ style: { display: "none" } }}
            style={{
              backgroundColor: FUNDO_CARTAO,
              marginTop: offsetTopo,
              marginLeft: offsetLateral,
              marginRight: offsetLateral,
              borderTopLeftRadius: estilosPapper.root.borderRadius,
              borderTopRightRadius: estilosPapper.root.borderRadius,
            }}
          >
            {ABAS.map((aba, indice) => (
              <Tab
                key={aba.rotulo}
                id={idAba(indice)}
                aria-controls={idPainel(indice)}
                label={aba.rotulo}
                aria-label={aba.rotulo}
                style={estiloAba(
                  indice === abaAtiva,
                  indice === 0,
                  indice === ABAS.length - 1,
                  telaPequena
                )}
              />
            ))}
          </Tabs>

          <div style={{ paddingTop: 30 }}>
            {!ABAS[abaAtiva].semFiltros && (
              <FiltrosPainel
                aba={ABAS[abaAtiva].periodo}
                semanal={!!ABAS[abaAtiva].filtroSemanal}
                vendasAte={!!ABAS[abaAtiva].filtroVendasAte}
              />
            )}
            {ABAS.map((aba, indice) =>
              visitadas.indexOf(indice) < 0 ? null : (
                <div
                  key={aba.rotulo}
                  role="tabpanel"
                  id={idPainel(indice)}
                  aria-labelledby={idAba(indice)}
                  hidden={indice !== abaAtiva}
                >
                  <PainelVisivelContext.Provider value={indice === abaAtiva}>
                    <LimiteErroTela titulo={aba.rotulo}>
                      <aba.conteudo />
                    </LimiteErroTela>
                  </PainelVisivelContext.Provider>
                </div>
              )
            )}
          </div>
        </FiltroHomeProvider>
      </PapperBlock>
    </LiberaAcesso>
  );
};

export default DashboardNovo;
